/**
 * RegionMonitoringTemplate.tsx — Region Monitoring. Covers: region_monitoring.
 *
 * The payload shape here is the same family as CompanyReportTemplate's and
 * CryptoWalletTemplate's — a single AI-generated report object, not a list
 * of broker records:
 *
 *   { ok, research_id, generated_at, source,
 *     query: { location, period_days, time_from, time_to, language, max_items },
 *     report: {
 *       scope: { requested_location, period_start, period_end, languages_searched[] },
 *       executive_summary: string,
 *       statistics: { relevant_items, potential_policy_violations,
 *         criticism_or_political_narratives, active_channels, high_severity_items },
 *       content_items[]: { title, platform, content_language, published_at,
 *         channel_name, category, is_potential_violation, severity, confidence,
 *         summary, evidence_excerpt, location: { name, ... }, item_id },
 *       active_channels[]: { name, platform, relevant_items_count,
 *         categories[], activity_summary, severity, location_relevance },
 *       geographic_findings[]: { location, items_count, basis, confidence, summary },
 *       assessment: { overall_level, summary, priority_items[], recommended_actions[] },
 *     } }
 *
 * region_monitoring_entities_extract's response is a SEPARATE row appended
 * to the SAME RequestMonitor (see backend's regionMonitoringEntitiesExtract
 * — it reuses the original monitor's requestId rather than creating a new
 * one), so apiDataEntities can carry a second entry with
 * `from: 'ars_region_monitoring_entities_extract'` alongside the original
 * `ars_region_monitoring` one:
 *
 *   { ok, research_id, cached,
 *     entities[]: { url, name, type, platform, confidence, relationship,
 *       related_item_ids[], related_items_count },
 *     contributors[]: { name, type, platform, confidence, content_url,
 *       profile_url, relationship, related_item_ids[], parent_entity_url,
 *       parent_entity_name, related_items_count },
 *     extraction_coverage: { total_items, coverage_percent,
 *       unmatched_item_ids[], items_with_entities, items_with_contributors,
 *       items_with_any_extraction, items_without_any_extraction },
 *     source: 'ars_region_monitoring_entities_extract' }
 *
 * extract() below scans every apiDataEntities row for BOTH shapes rather
 * than returning on the first `report` match, since the two can arrive in
 * either order (or the extraction one might not exist yet).
 *
 * Before this template existed, region_monitoring had no entry in
 * registry.ts's TYPE_MAP, so it fell through to PersonTemplate — built
 * around prepareCommonResult()'s broker-record shape, which finds nothing at
 * all in a report like this. The real content was reachable only by opening
 * RawCard's collapsed JSON at the bottom.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import {
  Chips, Cols, FieldTable, Flag, FlagTone, NoData, RawCard, ResCard, Sech, sentence,
  ShowMoreButton, Stack, Stats, useShowMore, useTokens, ValueList, Verdict, Warn,
} from '../ui/primitives'
import type { TemplateProps } from '../registry'

type ContentItem = {
  title?: string
  platform?: string
  language?: string
  publishedAt?: string
  channel?: string
  category?: string
  isViolation: boolean
  severity?: string
  confidence?: number
  summary?: string
  evidence?: string
  locationName?: string
}

type ActiveChannel = {
  name?: string
  platform?: string
  itemsCount?: number
  categories: string[]
  activitySummary?: string
  severity?: string
  locationRelevance?: string
}

type GeoFinding = {
  location?: string
  itemsCount?: number
  basis?: string
  confidence?: number
  summary?: string
}

type MonitoringEntity = {
  url?: string
  name?: string
  type?: string
  platform?: string
  confidence?: number
  relationship?: string
  relatedItemIds: string[]
  relatedItemsCount?: number
}

type Contributor = {
  name?: string
  type?: string
  platform?: string
  confidence?: number
  contentUrl?: string
  profileUrl?: string
  relationship?: string
  relatedItemIds: string[]
  parentEntityUrl?: string
  parentEntityName?: string
  relatedItemsCount?: number
}

type ExtractionCoverage = {
  totalItems?: number
  coveragePercent?: number
  itemsWithEntities?: number
  itemsWithContributors?: number
  itemsWithAnyExtraction?: number
  itemsWithoutAnyExtraction?: number
}

type RegionReport = {
  ok: boolean
  researchId?: string
  generatedAt?: string
  source?: string
  queryLocation?: string
  periodDays?: number
  language?: string
  maxItems?: number
  requestedLocation?: string
  periodStart?: string
  periodEnd?: string
  languagesSearched: string[]
  executiveSummary?: string
  relevantItems?: number
  policyViolations?: number
  criticismItems?: number
  activeChannelsCount?: number
  highSeverityItems?: number
  contentItems: ContentItem[]
  activeChannels: ActiveChannel[]
  geoFindings: GeoFinding[]
  overallLevel?: string
  assessmentSummary?: string
  priorityItems: string[]
  recommendedActions: string[]
  entities: MonitoringEntity[]
  contributors: Contributor[]
  extractionCoverage?: ExtractionCoverage
}

function normalizeEntities(raw: any): MonitoringEntity[] {
  return (Array.isArray(raw) ? raw : []).map((e: any) => ({
    url: e?.url || undefined,
    name: e?.name || undefined,
    type: e?.type || undefined,
    platform: e?.platform || undefined,
    confidence: typeof e?.confidence === 'number' ? e.confidence : undefined,
    relationship: e?.relationship || undefined,
    relatedItemIds: Array.isArray(e?.related_item_ids) ? e.related_item_ids : [],
    relatedItemsCount: typeof e?.related_items_count === 'number' ? e.related_items_count : undefined,
  }))
}

function normalizeContributors(raw: any): Contributor[] {
  return (Array.isArray(raw) ? raw : []).map((c: any) => ({
    name: c?.name || undefined,
    type: c?.type || undefined,
    platform: c?.platform || undefined,
    confidence: typeof c?.confidence === 'number' ? c.confidence : undefined,
    contentUrl: c?.content_url || undefined,
    profileUrl: c?.profile_url || undefined,
    relationship: c?.relationship || undefined,
    relatedItemIds: Array.isArray(c?.related_item_ids) ? c.related_item_ids : [],
    parentEntityUrl: c?.parent_entity_url || undefined,
    parentEntityName: c?.parent_entity_name || undefined,
    relatedItemsCount: typeof c?.related_items_count === 'number' ? c.related_items_count : undefined,
  }))
}

/** The region_monitoring_entities_extract row's shape — see this file's header comment. */
function extractExtraction(row: any): { entities: MonitoringEntity[]; contributors: Contributor[]; coverage?: ExtractionCoverage } {
  const cov = row.extraction_coverage || {}
  const hasCoverage = row.extraction_coverage && typeof row.extraction_coverage === 'object'
  return {
    entities: normalizeEntities(row.entities),
    contributors: normalizeContributors(row.contributors),
    coverage: hasCoverage
      ? {
          totalItems: typeof cov.total_items === 'number' ? cov.total_items : undefined,
          coveragePercent: typeof cov.coverage_percent === 'number' ? cov.coverage_percent : undefined,
          itemsWithEntities: typeof cov.items_with_entities === 'number' ? cov.items_with_entities : undefined,
          itemsWithContributors: typeof cov.items_with_contributors === 'number' ? cov.items_with_contributors : undefined,
          itemsWithAnyExtraction: typeof cov.items_with_any_extraction === 'number' ? cov.items_with_any_extraction : undefined,
          itemsWithoutAnyExtraction: typeof cov.items_without_any_extraction === 'number' ? cov.items_without_any_extraction : undefined,
        }
      : undefined,
  }
}

function extract(apiData: any): RegionReport | null {
  const apiEntities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  let base: RegionReport | null = null
  let extraction: { entities: MonitoringEntity[]; contributors: Contributor[]; coverage?: ExtractionCoverage } | null = null

  for (const e of apiEntities) {
    const rows: any[] = Array.isArray(e?.data) ? e.data : []
    for (const row of rows) {
      // ars_region_monitoring — the original report row.
      if (!base && row?.report && typeof row.report === 'object') {
        const report = row.report
        const scope = report.scope || {}
        const stats = report.statistics || {}
        const assessment = report.assessment || {}
        const query = row.query || {}

        base = {
          ok: row.ok !== false,
          researchId: row.research_id || undefined,
          generatedAt: row.generated_at || undefined,
          source: row.source || undefined,
          queryLocation: query.location || undefined,
          periodDays: typeof query.period_days === 'number' ? query.period_days : undefined,
          language: query.language || undefined,
          maxItems: typeof query.max_items === 'number' ? query.max_items : undefined,
          requestedLocation: scope.requested_location || undefined,
          periodStart: scope.period_start || undefined,
          periodEnd: scope.period_end || undefined,
          languagesSearched: Array.isArray(scope.languages_searched) ? scope.languages_searched : [],
          executiveSummary: report.executive_summary || undefined,
          relevantItems: typeof stats.relevant_items === 'number' ? stats.relevant_items : undefined,
          policyViolations: typeof stats.potential_policy_violations === 'number' ? stats.potential_policy_violations : undefined,
          criticismItems: typeof stats.criticism_or_political_narratives === 'number' ? stats.criticism_or_political_narratives : undefined,
          activeChannelsCount: typeof stats.active_channels === 'number' ? stats.active_channels : undefined,
          highSeverityItems: typeof stats.high_severity_items === 'number' ? stats.high_severity_items : undefined,
          contentItems: (Array.isArray(report.content_items) ? report.content_items : []).map((c: any) => ({
            title: c?.title || undefined,
            platform: c?.platform || undefined,
            language: c?.content_language || undefined,
            publishedAt: c?.published_at || undefined,
            channel: c?.channel_name || undefined,
            category: c?.category || undefined,
            isViolation: !!c?.is_potential_violation,
            severity: c?.severity || undefined,
            confidence: typeof c?.confidence === 'number' ? c.confidence : undefined,
            summary: c?.summary || undefined,
            evidence: c?.evidence_excerpt || undefined,
            locationName: c?.location?.name || undefined,
          })),
          activeChannels: (Array.isArray(report.active_channels) ? report.active_channels : []).map((ch: any) => ({
            name: ch?.name || undefined,
            platform: ch?.platform || undefined,
            itemsCount: typeof ch?.relevant_items_count === 'number' ? ch.relevant_items_count : undefined,
            categories: Array.isArray(ch?.categories) ? ch.categories : [],
            activitySummary: ch?.activity_summary || undefined,
            severity: ch?.severity || undefined,
            locationRelevance: ch?.location_relevance || undefined,
          })),
          geoFindings: (Array.isArray(report.geographic_findings) ? report.geographic_findings : []).map((g: any) => ({
            location: g?.location || undefined,
            itemsCount: typeof g?.items_count === 'number' ? g.items_count : undefined,
            basis: g?.basis || undefined,
            confidence: typeof g?.confidence === 'number' ? g.confidence : undefined,
            summary: g?.summary || undefined,
          })),
          overallLevel: assessment.overall_level || undefined,
          assessmentSummary: assessment.summary || undefined,
          priorityItems: Array.isArray(assessment.priority_items) ? assessment.priority_items : [],
          recommendedActions: Array.isArray(assessment.recommended_actions) ? assessment.recommended_actions : [],
          entities: [],
          contributors: [],
          extractionCoverage: undefined,
        }
        continue
      }

      // ars_region_monitoring_entities_extract — the appended follow-up row,
      // may land in the same apiDataEntities array as a second entry (see
      // this file's header comment), in either order relative to `report`.
      if (!extraction && (Array.isArray(row?.entities) || Array.isArray(row?.contributors))) {
        extraction = extractExtraction(row)
      }
    }
  }

  if (!base) return null
  if (extraction) {
    base.entities = extraction.entities
    base.contributors = extraction.contributors
    base.extractionCoverage = extraction.coverage
  }
  return base
}

/**
 * True once a region_monitoring_entities_extract follow-up row has already
 * landed on this result (same detection this file's own `extract()` uses:
 * a row carrying an `entities` or `contributors` array, even an empty one —
 * that's what a completed extraction response looks like, see this file's
 * header comment for the shape).
 *
 * Used by ResultView.tsx to permanently hide the "Extract entities" pivot
 * button once it's been run for this result — extraction is a one-time
 * follow-up per result_monitoring result, not something to offer again.
 */
export function hasEntitiesExtraction(apiData: any): boolean {
  const apiEntities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  return apiEntities.some((e) => {
    const rows: any[] = Array.isArray(e?.data) ? e.data : []
    return rows.some((row) => Array.isArray(row?.entities) || Array.isArray(row?.contributors))
  })
}

/** DD.MM.YYYY — same format CompanyReportTemplate/CryptoWalletTemplate use. */
function fmtDate(s?: string): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
}

function levelTone(level?: string): 'ok' | 'warn' | 'bad' {
  const v = String(level || '').toLowerCase()
  if (v.startsWith('low') || v.startsWith('minimal') || v.startsWith('none')) return 'ok'
  if (v.startsWith('high') || v.startsWith('critical') || v.startsWith('severe')) return 'bad'
  return 'warn'
}

function toFlagTone(t: 'ok' | 'warn' | 'bad'): FlagTone {
  return t === 'ok' ? 'ok' : t === 'bad' ? 'bad' : 'conflict'
}

/** "government_or_state_criticism" -> "Government or state criticism" */
function humanize(v?: string): string | undefined {
  if (!v) return undefined
  return sentence(v.replace(/_/g, ' ')) as React.ReactNode as string
}

/** One item flagged by the monitoring run — title, category/severity flags, meta line, summary, quoted evidence. */
function ContentItemList({ items }: { items: ContentItem[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 5)
  return (
    <>
      {visible.map((c, i) => (
        <Box key={i} sx={{ pt: i ? '12px' : 0, mt: i ? '12px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 200 }}>
              {c.title || 'Untitled item'}
            </Typography>
            <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {c.isViolation && <Flag tone="bad" label="Potential violation" />}
              {c.severity && <Flag tone={toFlagTone(levelTone(c.severity))} label={sentence(c.severity) as React.ReactNode as string} />}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[fmtDate(c.publishedAt), c.channel, c.platform, c.locationName].filter(Boolean).join(' · ') || ' '}
          </Typography>
          {c.category && (
            <Box sx={{ mt: '6px' }}>
              <Chips items={[humanize(c.category) || c.category]} tone="b" />
            </Box>
          )}
          {c.summary && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '6px', lineHeight: 1.5 }}>{c.summary}</Typography>
          )}
          {c.evidence && (
            <Typography
              sx={{
                fontSize: 11.5, color: tk.text3, mt: '6px', lineHeight: 1.5, fontStyle: 'italic',
                borderLeft: `2px solid ${tk.border}`, pl: '9px',
              }}
            >
              “{c.evidence}”
            </Typography>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

/** One outlet/channel the monitor saw activity on — name/platform, item count, severity, activity blurb, categories. */
function ChannelList({ items }: { items: ActiveChannel[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 5)
  return (
    <>
      {visible.map((c, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 160 }}>
              {c.name || 'Unknown channel'}
            </Typography>
            {c.severity && <Flag tone={toFlagTone(levelTone(c.severity))} label={sentence(c.severity) as React.ReactNode as string} />}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[c.platform, c.itemsCount !== undefined ? `${c.itemsCount} item${c.itemsCount === 1 ? '' : 's'}` : undefined]
              .filter(Boolean)
              .join(' · ') || ' '}
          </Typography>
          {c.activitySummary && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '5px', lineHeight: 1.5 }}>{c.activitySummary}</Typography>
          )}
          {c.categories.length > 0 && (
            <Box sx={{ mt: '6px' }}>
              <Chips items={c.categories.map((cat) => humanize(cat) || cat)} cap={6} />
            </Box>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

/** One place the monitor's findings cluster around — location name, item count, summary. */
function GeoFindingList({ items }: { items: GeoFinding[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 6)
  return (
    <>
      {visible.map((g, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 140 }}>
              {g.location || 'Unknown location'}
            </Typography>
            {g.itemsCount !== undefined && (
              <Box component="span" sx={{ background: tk.surface3, color: tk.text2, borderRadius: '20px', px: '7px', fontSize: 10, fontWeight: 700 }}>
                {g.itemsCount}
              </Box>
            )}
          </Box>
          {g.summary && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '4px', lineHeight: 1.5 }}>{g.summary}</Typography>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

/** One outlet/page/account the extraction step tied to the monitored content — name/link, type flag, related-item count, relationship blurb. */
function EntityList({ items }: { items: MonitoringEntity[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 6)
  const linkSx = { color: tk.text, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
  return (
    <>
      {visible.map((e, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 160 }}>
              {e.url ? (
                <Box component="a" href={e.url} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                  {e.name || e.url}
                </Box>
              ) : (
                e.name || 'Unknown entity'
              )}
            </Typography>
            {e.type && <Flag tone="primary" label={humanize(e.type) || e.type} />}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[e.platform, e.relatedItemsCount !== undefined ? `${e.relatedItemsCount} item${e.relatedItemsCount === 1 ? '' : 's'}` : undefined]
              .filter(Boolean)
              .join(' · ') || ' '}
          </Typography>
          {e.relationship && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '5px', lineHeight: 1.5 }}>{e.relationship}</Typography>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

/** One byline/author/account behind a specific piece of monitored content — name/profile link, parent outlet, relationship blurb, link to the item. */
function ContributorList({ items }: { items: Contributor[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 6)
  const linkSx = { color: tk.text, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
  return (
    <>
      {visible.map((c, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 160 }}>
              {c.profileUrl ? (
                <Box component="a" href={c.profileUrl} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                  {c.name || 'Unknown contributor'}
                </Box>
              ) : (
                c.name || 'Unknown contributor'
              )}
            </Typography>
            {c.type && <Flag tone="variant" label={humanize(c.type) || c.type} />}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[c.parentEntityName, c.platform].filter(Boolean).join(' · ') || ' '}
          </Typography>
          {c.relationship && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '5px', lineHeight: 1.5 }}>{c.relationship}</Typography>
          )}
          {c.contentUrl && (
            <Box sx={{ mt: '5px' }}>
              <Box
                component="a"
                href={c.contentUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 11, fontWeight: 600, color: '#2D89F4', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                View article →
              </Box>
            </Box>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

export function RegionMonitoringTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const r = React.useMemo(() => extract(apiData), [apiData])

  if (!r) {
    return <NoData title="No monitoring report returned" hint="The region monitoring run produced no output for this location." />
  }

  const primaryLocation = r.requestedLocation || r.queryLocation || 'Unknown region'
  const lTone = levelTone(r.overallLevel)

  const scopeRows = ([
    ['Requested location', primaryLocation],
    ['Period', [fmtDate(r.periodStart), fmtDate(r.periodEnd)].filter(Boolean).join(' – ') || undefined],
    ['Period length', r.periodDays !== undefined ? `${r.periodDays} day${r.periodDays === 1 ? '' : 's'}` : undefined],
    ['Language', r.language ? sentence(r.language) : undefined],
    ['Max items requested', r.maxItems !== undefined ? String(r.maxItems) : undefined],
    ['Research ID', r.researchId],
  ] as [string, React.ReactNode][]).filter(([, v]) => v)

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Location', value: primaryLocation },
            {
              label: 'Period',
              value: r.periodDays !== undefined ? `${r.periodDays} day${r.periodDays === 1 ? '' : 's'}` : '—',
            },
            { label: 'Relevant items', value: r.relevantItems ?? '—' },
            { label: 'Active channels', value: r.activeChannelsCount ?? '—' },
            { label: 'High severity', value: r.highSeverityItems ?? '—', tone: r.highSeverityItems ? 'bad' : undefined },
            ...(r.overallLevel ? [{ label: 'Overall level', value: sentence(r.overallLevel), tone: lTone }] : []),
          ]}
        />
      </ResCard>

      {!r.ok && (
        <ResCard>
          <Warn title="This report may be incomplete">
            The monitoring run was flagged as unsuccessful — treat the sections below as partial.
          </Warn>
        </ResCard>
      )}

      {r.overallLevel && (
        <ResCard title="Threat assessment" tone={lTone === 'ok' ? 'good' : lTone === 'bad' ? 'bad' : 'accent'}>
          <Verdict
            tone={lTone}
            title={(sentence(r.overallLevel) as React.ReactNode as string) || 'Unknown'}
            subtitle="model assessment"
            icon={(
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3 4 6v6c0 5 3.4 9.2 8 10 4.6-.8 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" />
              </svg>
            )}
          />
          {r.assessmentSummary && (
            <Typography sx={{ mt: '11px', fontSize: 12.5, lineHeight: 1.6, color: tk.text2 }}>{r.assessmentSummary}</Typography>
          )}
          {r.priorityItems.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.priorityItems.length}>Priority items</Sech>
              <ValueList rows={r.priorityItems.map((v) => ({ value: v }))} cap={10} />
            </Box>
          )}
          {r.recommendedActions.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.recommendedActions.length}>Recommended actions</Sech>
              <ValueList rows={r.recommendedActions.map((v) => ({ value: v }))} cap={10} />
            </Box>
          )}
        </ResCard>
      )}

      {r.executiveSummary && (
        <ResCard title="Executive summary">
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: tk.text2 }}>{r.executiveSummary}</Typography>
        </ResCard>
      )}

      <Cols n={2}>
        <Stack>
          <ResCard title="Scope">
            <FieldTable rows={scopeRows} />
            {r.languagesSearched.length > 0 && (
              <Box sx={{ mt: '12px' }}>
                <Sech count={r.languagesSearched.length}>Languages searched</Sech>
                <Chips items={r.languagesSearched} />
              </Box>
            )}
          </ResCard>
        </Stack>

        <Stack>
          <ResCard title="Statistics">
            <Stats
              items={[
                { label: 'Relevant items', value: r.relevantItems ?? '—' },
                { label: 'Policy violations', value: r.policyViolations ?? '—' },
                { label: 'Criticism / political', value: r.criticismItems ?? '—' },
                { label: 'High severity', value: r.highSeverityItems ?? '—' },
              ]}
            />
          </ResCard>
        </Stack>
      </Cols>

      {r.contentItems.length > 0 && (
        <ResCard title="Content items" count={r.contentItems.length}>
          <ContentItemList items={r.contentItems} />
        </ResCard>
      )}

      {(r.activeChannels.length > 0 || r.geoFindings.length > 0) && (
        <Cols n={2}>
          <Stack>
            {r.activeChannels.length > 0 ? (
              <ResCard title="Active channels" count={r.activeChannels.length}>
                <ChannelList items={r.activeChannels} />
              </ResCard>
            ) : (
              <Box />
            )}
          </Stack>
          <Stack>
            {r.geoFindings.length > 0 ? (
              <ResCard title="Geographic findings" count={r.geoFindings.length}>
                <GeoFindingList items={r.geoFindings} />
              </ResCard>
            ) : (
              <Box />
            )}
          </Stack>
        </Cols>
      )}

      {/* region_monitoring_entities_extract — appended to this same result
          after the fact (see this file's header comment); absent until the
          user actually runs that follow-up extraction. */}
      {(r.entities.length > 0 || r.contributors.length > 0) && (
        <Cols n={2}>
          <Stack>
            {r.entities.length > 0 ? (
              <ResCard title="Extracted entities" count={r.entities.length}>
                {r.extractionCoverage?.coveragePercent !== undefined && (
                  <Typography sx={{ fontSize: 11, color: tk.text3, mb: '10px' }}>
                    Entity extraction coverage: {r.extractionCoverage.coveragePercent}%
                    {r.extractionCoverage.itemsWithAnyExtraction !== undefined && r.extractionCoverage.totalItems !== undefined
                      ? ` (${r.extractionCoverage.itemsWithAnyExtraction}/${r.extractionCoverage.totalItems} items)`
                      : ''}
                  </Typography>
                )}
                <EntityList items={r.entities} />
              </ResCard>
            ) : (
              <Box />
            )}
          </Stack>
          <Stack>
            {r.contributors.length > 0 ? (
              <ResCard title="Contributors" count={r.contributors.length}>
                <ContributorList items={r.contributors} />
              </ResCard>
            ) : (
              <Box />
            )}
          </Stack>
        </Cols>
      )}

      <RawCard data={apiData} />
    </>
  )
}
